import { Suspense, lazy, type ComponentType } from "react";
import { createBrowserRouter, Navigate, Outlet, useLocation } from "react-router-dom";
import { Skeleton } from "@crc/ui";
import { useAuth, homeFor } from "./lib/auth.tsx";
import { AppShell } from "./shell/AppShell.tsx";
import { SignIn, MockSsoChooser } from "./pages/SignIn.tsx";
const AcceptInvite = lazy(() => import("./pages/AcceptInvite.tsx").then((m) => ({ default: m.AcceptInvite })));
const Apply = lazy(() => import("./pages/Apply.tsx").then((m) => ({ default: m.Apply })));
const Dashboard = lazy(() => import("./pages/Dashboard.tsx").then((m) => ({ default: m.Dashboard })));
const Companies = lazy(() => import("./pages/Companies.tsx").then((m) => ({ default: m.Companies })));
const CompanyDetail = lazy(() => import("./pages/CompanyDetail.tsx").then((m) => ({ default: m.CompanyDetail })));
const CompanyNew = lazy(() => import("./pages/CompanyNew.tsx").then((m) => ({ default: m.CompanyNew })));
const Ingredients = lazy(() => import("./pages/Ingredients.tsx").then((m) => ({ default: m.Ingredients })));
const IngredientDetail = lazy(() => import("./pages/IngredientDetail.tsx").then((m) => ({ default: m.IngredientDetail })));
const CompanyIngredientDetail = lazy(() => import("./pages/IngredientDetail.tsx").then((m) => ({ default: m.CompanyIngredientDetail })));
const Requests = lazy(() => import("./pages/Requests.tsx").then((m) => ({ default: m.Requests })));
const RequestDetail = lazy(() => import("./pages/Requests.tsx").then((m) => ({ default: m.RequestDetail })));
const RequestNew = lazy(() => import("./pages/Requests.tsx").then((m) => ({ default: m.RequestNew })));
const Products = lazy(() => import("./pages/Products.tsx").then((m) => ({ default: m.Products })));
const ProductDetail = lazy(() => import("./pages/Products.tsx").then((m) => ({ default: m.ProductDetail })));
const Facilities = lazy(() => import("./pages/Locations.tsx").then((m) => ({ default: m.Facilities })));
const FacilityDetail = lazy(() => import("./pages/Locations.tsx").then((m) => ({ default: m.FacilityDetail })));
const Plants = lazy(() => import("./pages/Locations.tsx").then((m) => ({ default: m.Plants })));
const PlantDetail = lazy(() => import("./pages/Locations.tsx").then((m) => ({ default: m.PlantDetail })));
const Productions = lazy(() => import("./pages/Productions.tsx").then((m) => ({ default: m.Productions })));
const ProductionDetail = lazy(() => import("./pages/Productions.tsx").then((m) => ({ default: m.ProductionDetail })));
const Inspections = lazy(() => import("./pages/Inspections.tsx").then((m) => ({ default: m.Inspections })));
const InspectionDetail = lazy(() => import("./pages/Inspections.tsx").then((m) => ({ default: m.InspectionDetail })));
const InspectionNew = lazy(() => import("./pages/InspectionNew.tsx").then((m) => ({ default: m.InspectionNew })));
const MashgiachApp = lazy(() => import("./pages/MashgiachApp.tsx").then((m) => ({ default: m.MashgiachApp })));
const Certificates = lazy(() => import("./pages/Certificates.tsx").then((m) => ({ default: m.Certificates })));
const CertificateDetail = lazy(() => import("./pages/Certificates.tsx").then((m) => ({ default: m.CertificateDetail })));
const Billing = lazy(() => import("./pages/Billing.tsx").then((m) => ({ default: m.Billing })));
const InvoiceDetail = lazy(() => import("./pages/Billing.tsx").then((m) => ({ default: m.InvoiceDetail })));
const InvoiceLink = lazy(() => import("./pages/InvoiceLink.tsx").then((m) => ({ default: m.InvoiceLink })));
const Desk = lazy(() => import("./pages/Desk.tsx").then((m) => ({ default: m.Desk })));
const Reports = lazy(() => import("./pages/Reports.tsx").then((m) => ({ default: m.Reports })));
const PractitionerDetail = lazy(() => import("./pages/Practitioners.tsx").then((m) => ({ default: m.PractitionerDetail })));
const Practitioners = lazy(() => import("./pages/Practitioners.tsx").then((m) => ({ default: m.Practitioners })));
const Stam = lazy(() => import("./pages/Stam.tsx").then((m) => ({ default: m.Stam })));
const StamDetail = lazy(() => import("./pages/Stam.tsx").then((m) => ({ default: m.StamDetail })));
const StamNew = lazy(() => import("./pages/Stam.tsx").then((m) => ({ default: m.StamNew })));
const Directory = lazy(() => import("./pages/Directory.tsx").then((m) => ({ default: m.Directory })));
const Wine = lazy(() => import("./pages/Wine.tsx").then((m) => ({ default: m.Wine })));
const WineDetail = lazy(() => import("./pages/Wine.tsx").then((m) => ({ default: m.WineDetail })));
const Esrogim = lazy(() => import("./pages/Esrogim.tsx").then((m) => ({ default: m.Esrogim })));
const Profile = lazy(() => import("./pages/Profile.tsx").then((m) => ({ default: m.Profile })));
const Admin = lazy(() => import("./pages/admin/Admin.tsx").then((m) => ({ default: m.Admin })));
const Import = lazy(() => import("./pages/admin/Import.tsx").then((m) => ({ default: m.Import })));
const CompanyHome = lazy(() => import("./pages/CompanyHome.tsx").then((m) => ({ default: m.CompanyHome })));
const Scan = lazy(() => import("./pages/Scan.tsx").then((m) => ({ default: m.Scan })));
const Search = lazy(() => import("./pages/Search.tsx").then((m) => ({ default: m.Search })));
const Onboarding = lazy(() => import("./pages/Onboarding.tsx").then((m) => ({ default: m.Onboarding })));
import { NotFound } from "./pages/NotFound.tsx";
const Listings = lazy(() => import("./pages/Listings.tsx").then((m) => ({ default: m.Listings })));
const ListingDetail = lazy(() => import("./pages/Listings.tsx").then((m) => ({ default: m.ListingDetail })));
const ListingNew = lazy(() => import("./pages/Listings.tsx").then((m) => ({ default: m.ListingNew })));
const Authorizations = lazy(() => import("./pages/Authorizations.tsx").then((m) => ({ default: m.Authorizations })));
const AuthorizationDetail = lazy(() => import("./pages/Authorizations.tsx").then((m) => ({ default: m.AuthorizationDetail })));
const AuthorizationNew = lazy(() => import("./pages/Authorizations.tsx").then((m) => ({ default: m.AuthorizationNew })));
const Compliance = lazy(() => import("./pages/Compliance.tsx").then((m) => ({ default: m.Compliance })));
const IssueDetail = lazy(() => import("./pages/Compliance.tsx").then((m) => ({ default: m.IssueDetail })));
const IssueNew = lazy(() => import("./pages/Compliance.tsx").then((m) => ({ default: m.IssueNew })));
const ComplaintNew = lazy(() => import("./pages/Compliance.tsx").then((m) => ({ default: m.ComplaintNew })));
const Complain = lazy(() => import("./pages/Public.tsx").then((m) => ({ default: m.Complain })));
const OpenProduction = lazy(() => import("./pages/Public.tsx").then((m) => ({ default: m.OpenProduction })));
const CompleteIngredients = lazy(() => import("./pages/Public.tsx").then((m) => ({ default: m.CompleteIngredients })));
const Schedule = lazy(() => import("./pages/Schedule.tsx").then((m) => ({ default: m.Schedule })));
const Events = lazy(() => import("./pages/Events.tsx").then((m) => ({ default: m.Events })));
const EventDetail = lazy(() => import("./pages/Events.tsx").then((m) => ({ default: m.EventDetail })));
const PurchaseOrders = lazy(() => import("./pages/PurchaseOrders.tsx").then((m) => ({ default: m.PurchaseOrders })));
const Compensation = lazy(() => import("./pages/Compensation.tsx").then((m) => ({ default: m.Compensation })));
const MashgiachInvoiceDetail = lazy(() => import("./pages/Compensation.tsx").then((m) => ({ default: m.MashgiachInvoiceDetail })));
const InvoiceNew = lazy(() => import("./pages/Compensation.tsx").then((m) => ({ default: m.InvoiceNew })));
const Seals = lazy(() => import("./pages/Seals.tsx").then((m) => ({ default: m.Seals })));
const RollDetail = lazy(() => import("./pages/Seals.tsx").then((m) => ({ default: m.RollDetail })));
const Membership = lazy(() => import("./pages/Membership.tsx").then((m) => ({ default: m.Membership })));
const MemberDetail = lazy(() => import("./pages/MemberDetail.tsx").then((m) => ({ default: m.MemberDetail })));
const PortalShell = lazy(() => import("./pages/portal/Shell.tsx").then((m) => ({ default: m.PortalShell })));
const PortalSignIn = lazy(() => import("./pages/portal/Auth.tsx").then((m) => ({ default: m.PortalSignIn })));
const PortalAccept = lazy(() => import("./pages/portal/Auth.tsx").then((m) => ({ default: m.PortalAccept })));
const PortalForgot = lazy(() => import("./pages/portal/Auth.tsx").then((m) => ({ default: m.PortalForgot })));
const PortalReset = lazy(() => import("./pages/portal/Auth.tsx").then((m) => ({ default: m.PortalReset })));
const PortalHome = lazy(() => import("./pages/portal/Home.tsx").then((m) => ({ default: m.PortalHome })));
const PortalMembership = lazy(() => import("./pages/portal/MembershipPage.tsx").then((m) => ({ default: m.PortalMembership })));
const PortalProfile = lazy(() => import("./pages/portal/Profile.tsx").then((m) => ({ default: m.PortalProfile })));
const PortalQuestions = lazy(() => import("./pages/portal/Questions.tsx").then((m) => ({ default: m.PortalQuestions })));
const PortalQuestionNew = lazy(() => import("./pages/portal/Questions.tsx").then((m) => ({ default: m.PortalQuestionNew })));
const PortalQuestionDetail = lazy(() => import("./pages/portal/Questions.tsx").then((m) => ({ default: m.PortalQuestionDetail })));

/** Each page is its own chunk (NON-FUNCTIONAL.md → first paint); this is what shows for the few hundred milliseconds a chunk takes to arrive. */
function PageFallback() {
  return (
    <div className="stack" aria-busy="true" aria-label="Loading">
      <Skeleton h={28} w="40%" />
      <Skeleton h={14} w="60%" />
      <Skeleton h={240} />
    </div>
  );
}
const L = (C: ComponentType) => <Suspense fallback={<PageFallback />}><C /></Suspense>;

function RequireAuth() {
  const { me, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div className="auth-wrap">
        <div className="auth-card stack">
          <Skeleton h={40} />
          <Skeleton h={14} w="60%" />
        </div>
      </div>
    );
  }
  if (!me) return <Navigate to={`/sign-in?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <Outlet />;
}

function Home() {
  const { me } = useAuth();
  if (me?.userType === "company") return L(CompanyHome);
  if (me && me.userType !== "internal") return <Navigate to={homeFor(me)} replace />;
  return L(Dashboard);
}

export const router = createBrowserRouter([
  { path: "/sign-in", element: <SignIn /> },
  { path: "/sign-in/mock", element: <MockSsoChooser /> },
  { path: "/accept-invite", element: L(AcceptInvite) },
  { path: "/apply", element: L(Apply) },
  { path: "/complain", element: L(Complain) },
  { path: "/open/:token", element: L(OpenProduction) },
  { path: "/complete/:token", element: L(CompleteIngredients) },
  { path: "/invoice/:token", element: L(InvoiceLink) },
  // the member portal: its own sign-in and shell, never the staff session
  { path: "/portal/sign-in", element: L(PortalSignIn) },
  { path: "/portal/accept", element: L(PortalAccept) },
  { path: "/portal/forgot", element: L(PortalForgot) },
  { path: "/portal/reset", element: L(PortalReset) },
  {
    element: L(PortalShell),
    children: [
      { path: "/portal", element: L(PortalHome) },
      { path: "/portal/membership", element: L(PortalMembership) },
      { path: "/portal/profile", element: L(PortalProfile) },
      { path: "/portal/questions", element: L(PortalQuestions) },
      { path: "/portal/questions/new", element: L(PortalQuestionNew) },
      { path: "/portal/questions/:id", element: L(PortalQuestionDetail) },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <Suspense fallback={<PageFallback />}><AppShell /></Suspense>,
        children: [
          { path: "/", element: <Home /> },
          { path: "/desk", element: L(Desk) },
          { path: "/companies", element: L(Companies) },
          { path: "/companies/new", element: L(CompanyNew) },
          { path: "/companies/:id", element: L(CompanyDetail) },
          { path: "/ingredients", element: L(Ingredients) },
          { path: "/ingredients/:id", element: L(IngredientDetail) },
          { path: "/company-ingredients/:id", element: L(CompanyIngredientDetail) },
          { path: "/requests", element: L(Requests) },
          { path: "/requests/new", element: L(RequestNew) },
          { path: "/requests/:id", element: L(RequestDetail) },
          { path: "/products", element: L(Products) },
          { path: "/products/:id", element: L(ProductDetail) },
          { path: "/facilities", element: L(Facilities) },
          { path: "/facilities/:id", element: L(FacilityDetail) },
          { path: "/plants", element: L(Plants) },
          { path: "/plants/:id", element: L(PlantDetail) },
          { path: "/productions", element: L(Productions) },
          { path: "/productions/:id", element: L(ProductionDetail) },
          { path: "/inspections", element: L(Inspections) },
          { path: "/inspections/new", element: L(InspectionNew) },
          { path: "/inspections/:id", element: L(InspectionDetail) },
          { path: "/app", element: L(MashgiachApp) },
          { path: "/certificates", element: L(Certificates) },
          { path: "/certificates/:id", element: L(CertificateDetail) },
          { path: "/seals", element: L(Seals) },
          { path: "/seals/rolls/:id", element: L(RollDetail) },
          { path: "/schedule", element: L(Schedule) },
          { path: "/events", element: L(Events) },
          { path: "/events/:id", element: L(EventDetail) },
          { path: "/purchase-orders", element: L(PurchaseOrders) },
          { path: "/compensation", element: L(Compensation) },
          { path: "/compensation/new", element: L(InvoiceNew) },
          { path: "/compensation/:id", element: L(MashgiachInvoiceDetail) },
          { path: "/listings", element: L(Listings) },
          { path: "/listings/new", element: L(ListingNew) },
          { path: "/listings/:id", element: L(ListingDetail) },
          { path: "/authorizations", element: L(Authorizations) },
          { path: "/authorizations/new", element: L(AuthorizationNew) },
          { path: "/authorizations/:id", element: L(AuthorizationDetail) },
          { path: "/compliance", element: L(Compliance) },
          { path: "/compliance/new", element: L(IssueNew) },
          { path: "/compliance/complaints/new", element: L(ComplaintNew) },
          { path: "/compliance/:id", element: L(IssueDetail) },
          { path: "/membership", element: L(Membership) },
          { path: "/membership/members/:id", element: L(MemberDetail) },
          { path: "/billing", element: L(Billing) },
          { path: "/billing/invoices/:id", element: L(InvoiceDetail) },
          { path: "/reports", element: L(Reports) },
          { path: "/practitioners", element: L(Practitioners) },
          { path: "/practitioners/:id", element: L(PractitionerDetail) },
          { path: "/stam", element: L(Stam) },
          { path: "/stam/new", element: L(StamNew) },
          { path: "/stam/:id", element: L(StamDetail) },
          { path: "/directory", element: L(Directory) },
          { path: "/wine", element: L(Wine) },
          { path: "/wine/:id", element: L(WineDetail) },
          { path: "/esrogim", element: L(Esrogim) },
          { path: "/profile", element: L(Profile) },
          { path: "/import", element: L(Import) },
          { path: "/scan", element: L(Scan) },
          { path: "/search", element: L(Search) },
          { path: "/onboarding", element: L(Onboarding) },
          { path: "/admin/*", element: L(Admin) },
          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
]);
